'use client'
import TruncatedTypography from '@/src/components/common/ui/truncatedTypography'
import { Airline } from '@/src/hooks/srp/airlineFilterManager'
import { DeviceType } from '@/src/types/deviceType'
import { Box, Button, Checkbox, Tooltip, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import React, { useState } from 'react'

interface AirlinesFilterProps {
    airlines?: Airline[]
    onAirlineSelect: (airlineId: string) => void
    onReset: () => void
    shouldShowReset: boolean
    title: string
    deviceType: DeviceType
}

const AirlinesFilter: React.FC<AirlinesFilterProps> = ({
    airlines,
    onAirlineSelect,
    onReset,
    shouldShowReset,
    title,
    deviceType
}) => {
    const [showAllAirlines, setShowAllAirlines] = useState(false)
    const t = useTranslations('common')
    const INITIAL_AIRLINES_COUNT = 5
    const shouldShowMoreButton = airlines && airlines.length > INITIAL_AIRLINES_COUNT
    const displayedAirlines = showAllAirlines ? airlines : airlines?.slice(0, INITIAL_AIRLINES_COUNT)
    const remainingCount = airlines ? airlines.length - INITIAL_AIRLINES_COUNT : 0

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'grey.900' }}>
                    {title}
                </Typography>
                {shouldShowReset && (
                    <Button
                        variant="text"
                        size="small"
                        onClick={onReset}
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: 'brand.primary',
                            p: 0,
                            minWidth: 'auto',
                            '&:hover': {
                                bgcolor: 'transparent',
                                color: 'brand.primaryHover'
                            }
                        }}
                    >
                        {t('reset')}
                    </Button>
                )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Multi-airline option */}
                {/* <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer'
                    }}
                    onClick={() => onMultiAirlineToggle(!showMultiAirline)}
                >
                    <Checkbox
                        size="small"
                        checked={showMultiAirline}
                        onChange={e => onMultiAirlineToggle(e.target.checked)}
                        sx={{
                            color: 'grey.400',
                            '&.Mui-checked': {
                                color: 'brand.primary'
                            },
                            p: 0.5
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            ml: 1.5,
                            fontSize: '0.875rem',
                            color: 'grey.700'
                        }}
                    >
                        Show multi-airline itineraries
                    </Typography>
                </Box> */}

                {/* Individual airlines */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {displayedAirlines?.map(airline => (
                        <Box
                            key={airline.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                position: 'relative',
                                '&:hover .airline-only-text': {
                                    opacity: 1
                                }
                            }}
                            onClick={() => onAirlineSelect(airline.id)}
                        >
                            <Checkbox
                                // size="small"
                                checked={!!airline.isSelected}
                                // onChange={() => onAirlineSelect(airline.id)}
                                sx={{
                                    color: 'grey.400',
                                    '&.Mui-checked': {
                                        color: 'brand.primary'
                                    },
                                    p: 0.5
                                }}
                            />
                            <Box
                                sx={{
                                    ml: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flex: 1
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        component="img"
                                        src={airline.logoUrl}
                                        alt={airline.name}
                                        sx={{
                                            height: 16,
                                            width: 24,
                                            objectFit: 'contain'
                                        }}
                                    />
                                    <Box sx={{ position: 'relative' }}>
                                        <Tooltip title={airline.name} followCursor>
                                            <TruncatedTypography
                                                fontSize={'0.875rem'}
                                                maxWidth={deviceType === 'MOBILE' ? 200 : 180}
                                                maxLines={1}
                                                variant="body2"
                                                color="#333333"
                                            >
                                                {airline.name}
                                                {/* <Typography
                                                component="span"
                                                className="airline-only-text"
                                                sx={{
                                                    ml: 0.5,
                                                    fontSize: '0.75rem',
                                                    color: 'brand.primary',
                                                    fontWeight: 600,
                                                    opacity: 0,
                                                    transition: 'opacity 0.2s'
                                                }}
                                            >
                                                {t('only')}
                                            </Typography> */}
                                            </TruncatedTypography>
                                        </Tooltip>
                                    </Box>
                                </Box>
                                {/* {deviceType === 'MOBILE' && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                            color: 'grey.900'
                                        }}
                                    >
                                        {airline.price}
                                    </Typography>
                                )} */}
                            </Box>
                        </Box>
                    ))}
                </Box>

                {/* Show more/less button */}
                {shouldShowMoreButton && (
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => setShowAllAirlines(!showAllAirlines)}
                        sx={{
                            fontWeight: 600,
                            color: 'brand.primary',
                            p: 0,
                            minWidth: 'auto',
                            mt: 1,
                            '&:hover': {
                                bgcolor: 'transparent',
                                color: 'brand.primaryHover'
                            }
                        }}
                    >
                        {showAllAirlines ? t('showLess') : t('showCountMoreAirlines', { count: remainingCount })}
                    </Button>
                )}
            </Box>
        </Box>
    )
}

export { AirlinesFilter }
export type { Airline, AirlinesFilterProps }
